/**
 * RouteAlertsPanel — Iter-91 · Operations Command Center.
 *
 * Shows every delayed-route alert visible to the caller (region-scoped by
 * the backend). Each alert exposes an Acknowledge button that stops the
 * 15-minute escalation timer. Alerts that have escalated show a red badge.
 *
 * Data source: `GET /api/operations/route-alerts` — refreshes every 30 s
 * alongside the map.
 */
import { useEffect, useState, useRef } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { AlertTriangle, CheckCircle2, Siren, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 30000;

export default function RouteAlertsPanel() {
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState({ active: 0, escalated: 0, acked_today: 0, delayed_routes_today: 0 });
  const [busy, setBusy] = useState(null);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const r = await fetch(`${API}/api/operations/route-alerts`, { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      setAlerts(d.alerts || []);
      setCounts(d.counts || { active: 0, escalated: 0, acked_today: 0, delayed_routes_today: 0 });
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  const ack = async (alertId) => {
    setBusy(alertId);
    try {
      const r = await fetch(`${API}/api/operations/route-alerts/${alertId}/acknowledge`, {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) {
        toast.success('Alert acknowledged — escalation stopped');
        load();
      } else {
        toast.error('Acknowledge failed');
      }
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  return (
    <Card className="bg-zinc-900 border border-amber-500/40 p-4" data-testid="ops-route-alerts-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Siren className="h-4 w-4 text-amber-400" />
          <h3 className="text-white font-black text-base" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            DELAYED ROUTE ALERTS
          </h3>
          <Badge className="bg-amber-700 text-white text-[10px] uppercase tracking-widest">SMS · Auto-escalate 15m</Badge>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="text-amber-400" data-testid="ops-alerts-active">
            Active: <b>{counts.active}</b>
          </span>
          <span className="text-red-400" data-testid="ops-alerts-escalated">
            Escalated: <b>{counts.escalated}</b>
          </span>
          <span className="text-emerald-400" data-testid="ops-alerts-acked-today">
            Acked today: <b>{counts.acked_today}</b>
          </span>
          <button type="button" onClick={load} className="text-zinc-500 hover:text-white" data-testid="ops-alerts-refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {alerts.length === 0 ? (
        <div className="p-3 rounded-lg border border-dashed border-zinc-700 text-center text-zinc-500 text-sm" data-testid="ops-alerts-empty">
          No delayed-route alerts. Every crew is running on-track.
        </div>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {alerts.map((a) => {
            const isAcked = !!a.acked_at;
            const isEscalated = !!a.escalated_at;
            return (
              <div
                key={a.alert_id}
                className="p-2.5 rounded border flex items-center justify-between gap-3 flex-wrap"
                style={{
                  borderColor: isAcked ? 'rgba(16,185,129,0.4)' : isEscalated ? 'rgba(239,68,68,0.5)' : 'rgba(245,158,11,0.4)',
                  backgroundColor: isAcked ? 'rgba(16,185,129,0.05)' : '#0a0a0a',
                }}
                data-testid={`ops-alert-row-${a.alert_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white text-sm font-semibold truncate">
                      {a.crew_leader_name} <span className="text-zinc-500 font-mono text-[11px]">({a.badge_number})</span>
                    </p>
                    <Badge className="bg-zinc-800 text-[10px]">Region {a.region_label}</Badge>
                    {isEscalated && !isAcked && (
                      <Badge className="bg-red-700 text-white text-[10px]">ESCALATED</Badge>
                    )}
                    {isAcked && (
                      <Badge className="bg-emerald-700 text-white text-[10px]">ACKNOWLEDGED</Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-0.5">
                    {a.property_name} · Stop <b className="text-white">{a.current_stop}</b> of <b className="text-white">{a.total_stops}</b> · ETA {(a.eta_iso || '').slice(11, 16)}
                  </p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    Fired {(a.created_at || '').slice(11, 16)}
                    {isAcked ? ` · Acked ${(a.acked_at || '').slice(11, 16)} by ${a.acked_by_name || 'ops'}` : ''}
                    {isEscalated ? ` · Escalated ${(a.escalated_at || '').slice(11, 16)}` : ''}
                  </p>
                </div>
                {!isAcked && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => ack(a.alert_id)}
                    disabled={busy === a.alert_id}
                    className="font-bold h-8 px-3"
                    style={{ backgroundColor: '#10b981', color: '#0a0a0a' }}
                    data-testid={`ops-alert-ack-${a.alert_id}`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                    {busy === a.alert_id ? 'Acking…' : 'Acknowledge'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <p className="text-[10px] text-zinc-500 mt-2 flex items-center gap-1">
        <AlertTriangle className="h-3 w-3" />
        Unacknowledged alerts escalate to the Operations Director via SMS after 15 minutes.
      </p>
    </Card>
  );
}
